import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { parsePhoneNumber } from 'libphonenumber-js';
import Swal from 'sweetalert2';
import { submitDataOrder, getNetworkPackages, checkMessaging, checkExistingRecipient } from '../services/firebaseService';

const DataOrderForm = ({ network }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    recipientNumber: '',
    momoNumber: '',
    packageId: '',
    network: network || 'mtn'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Fetch network packages on component mount
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoadingPackages(true);
        const { packages } = await getNetworkPackages();

        // Filter packages for the selected network
        const networkKey = network?.toLowerCase() || 'mtn';
        if (packages && Array.isArray(packages)) {
          const filteredPackages = packages.filter(
            pkg => pkg.network === networkKey
          );
          setPackages(filteredPackages);

          if (filteredPackages.length === 0) {
            setError('No packages found for this network');
          }
        } else {
          setPackages([]);
          setError('No packages found');
        }
      } catch (err) {
        console.error('Error fetching packages:', err);
        setError('Failed to load data packages');
        setPackages([]);
      } finally {
        setLoadingPackages(false);
      }
    };

    fetchPackages();
  }, [network]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');

    // When package is selected, find and set the selected package details
    if (name === 'packageId' && value) {
      const pkg = packages.find(p => p.volume.toString() === value);
      setSelectedPackage(pkg || null);
    } else if (name === 'packageId' && !value) {
      setSelectedPackage(null);
    }
  };

  const validatePhoneNumberLength = (phoneNumber) => {
    // Remove spaces and special characters
    const cleaned = phoneNumber.replace(/\s+/g, '');

    if (cleaned.startsWith('233')) {
      return cleaned.length === 12;
    } else if (cleaned.startsWith('0')) {
      return cleaned.length === 10;
    }
    return false;
  };

  const validateForm = () => {
    if (!formData.recipientNumber) {
      setError("Recipient's number is required");
      return false;
    }
    if (!formData.momoNumber) {
      setError('Momo wallet number is required');
      return false;
    }
    if (!formData.packageId) {
      setError('Please select a data package');
      return false;
    }

    // Validate phone number lengths
    if (!validatePhoneNumberLength(formData.recipientNumber)) {
      setError('Recipient number must be 10 digits (0XXXXXXXXX) or 12 digits (233XXXXXXXXX)');
      return false;
    }

    if (!validatePhoneNumberLength(formData.momoNumber)) {
      setError('Momo number must be 10 digits (0XXXXXXXXX) or 12 digits (233XXXXXXXXX)');
      return false;
    }

    // Validate phone numbers
    try {
      const recipientPN = parsePhoneNumber(formData.recipientNumber, 'GH');
      if (!recipientPN || !recipientPN.isValid()) {
        setError('Please enter a valid recipient phone number');
        return false;
      }

      const walletPN = parsePhoneNumber(formData.momoNumber, 'GH');
      if (!walletPN || !walletPN.isValid()) {
        setError('Please enter a valid momo wallet number');
        return false;
      }
    } catch (err) {
      setError('Please enter valid phone numbers');
      return false;
    }

    return true;
  };

  // Calculate fee and price
  // The 'price' from Firestore is actually the total price
  // Fee = 2.5% of total price
  // Actual price (after fee deduction) = total price - fee
  const calculateFee = (totalPrice) => {
    const fee = totalPrice * 0.02;
    return fee.toFixed(2);
  };

  const calculateActualPrice = (totalPrice) => {
    const fee = totalPrice * 0.02;
    const actualPrice = totalPrice + fee;
    return actualPrice.toFixed(2);
  };

  const processCheckout = async () => {
    try {
      // Parse phone numbers to E.164 format
      const recipientPN = parsePhoneNumber(formData.recipientNumber, 'GH');
      const walletPN = parsePhoneNumber(formData.momoNumber, 'GH');

      const recipient = recipientPN.number;
      const telephone = recipientPN.number;
      const wallet = walletPN.number.replace(/\D/g, '');
      const walletNational = walletPN.nationalNumber;

      // Prepare order data
      const totalPrice = selectedPackage?.price || 0;
      const fee = parseFloat(calculateFee(totalPrice));
      const actualPrice = parseFloat(calculateActualPrice(totalPrice));
      const volume = selectedPackage?.volume;

      // Check if recipient exists (for MTN only)
      // if (formData.network.toLowerCase() === 'mtn') {
      //   const recipientId = recipientPN.number.replace(/\D/g, '');
      //   const exists = await checkExistingRecipient(recipientId);

      //   if (!exists) {
      //     Swal.fire({
      //       icon: 'error',
      //       title: 'Error',
      //       text: 'We can only serve existing customers for MTN'
      //     });
      //     setLoading(false);
      //     return;
      //   }
      // }

      // Prepare payload for backend
      // price, volume, recipient, network, telephone, referral_code, customer_id, fees, total_price
      const payload = {
        price: totalPrice,
        volume,
        recipient,
        network: formData.network,
        telephone,
        wallet,
        fees: fee,
        total_price: totalPrice
      };

      // Submit order and get payment URL
      const result = await submitDataOrder(payload);
      console.log("result ", result)
      // Check if the response indicates an error
      if (result.status === false) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: result.message || 'Failed to process order. Please try again.'
        });
        setLoading(false);
        return;
      }

      const authUrl = result?.info?.data?.authorization_url;

      // If payment gateway URL is returned, redirect to it
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        // Otherwise, navigate to success page
        navigate('/success');
      }
    } catch (err) {
      setError(err.message || 'Failed to process checkout. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Check for any messaging/alerts before proceeding
      const networkKey = formData.network.toLowerCase();
      const messaging = await checkMessaging(networkKey);

      if (messaging && messaging.status) {
        // Show alert message to user
        const result = await Swal.fire({
          title: 'Message',
          icon: 'info',
          text: messaging.message,
          reverseButtons: true,
          showCancelButton: true,
          confirmButtonText: 'Proceed',
          cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
          await processCheckout();
        } else {
          setLoading(false);
        }
      } else {
        await processCheckout();
      }
    } catch (err) {
      setError(err.message || 'Failed to submit order. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white p-6 rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-4 text-center capitalize">
        Purchase {formData.network} Data
      </h2>

      {success && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          Order submitted successfully!
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="recipientNumber" className="block text-sm font-medium text-gray-700 mb-2">
            Enter Recipient's Number
          </label>
          <input
            type="tel"
            id="recipientNumber"
            name="recipientNumber"
            value={formData.recipientNumber}
            onChange={handleInputChange}
            placeholder="0XX XXX XXXX"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          />
        </div>

        <div className="mb-4">
          <label htmlFor="momoNumber" className="block text-sm font-medium text-gray-700 mb-2">
            Enter Momo Wallet Number
          </label>
          <input
            type="tel"
            id="momoNumber"
            name="momoNumber"
            value={formData.momoNumber}
            onChange={handleInputChange}
            placeholder="0XX XXX XXXX"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          />
        </div>

        <div className="mb-4">
          <label htmlFor="packageId" className="block text-sm font-medium text-gray-700 mb-2">
            Data Package
          </label>
          <select
            id="packageId"
            name="packageId"
            value={formData.packageId}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading || loadingPackages}
          >
            <option value="">
              {loadingPackages ? 'Loading packages...' : 'Select a package'}
            </option>
            {packages.map((pkg, index) => (
              <option key={index} value={pkg.volume}>
                {pkg.volume}GB - GHS {pkg.price}
              </option>
            ))}
          </select>
        </div>

        {/* Package Details */}
        {selectedPackage && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Package Details</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Volume:</span>
                <span className="text-sm font-medium text-gray-900">{selectedPackage.volume} GB</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Price:</span>
                <span className="text-sm font-medium text-gray-900">GHS {selectedPackage.price.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Fees:</span>
                <span className="text-sm font-medium text-gray-900">GHS {calculateFee(selectedPackage.price)}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                <span className="text-sm font-semibold text-gray-700">Total Price:</span>
                <span className="text-base font-bold text-blue-600">
                  GHS {(selectedPackage.price + parseFloat(calculateFee(selectedPackage.price))).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !formData.recipientNumber || !formData.momoNumber || !formData.packageId}
          className="w-full bg-blue-600 text-white py-3 rounded-md font-medium hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed mt-4"
        >
          {loading ? 'Processing...' : 'Make Payment'}
        </button>
      </form>
    </div>
  );
};

export default DataOrderForm;
